import React, { useState } from 'react'
import { useAuth } from 'wasp/client/auth'
import { recordInterestAuthed, recordInterestEmail } from 'wasp/client/operations'
import { Card, CardContent, CardHeader } from '../v32/components/ui/card'
import { Button } from '../v32/components/ui/button'
import { Input } from '../v32/components/ui/input'
import { Label } from '../v32/components/ui/label'

export default function Receipt2RecipeComingSoon() {
  const { data: user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const handleTap = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setMessage(null)
    
    try {
      await recordInterestAuthed()
      setMessage("Thanks! You'll be first to know when it launches.")
    } catch (error: any) {
      setMessage(error?.message || "Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isSubmitting) return
    setIsSubmitting(true)
    setMessage(null)
    
    const formData = new FormData(e.currentTarget)
    const email = formData.get("email") as string
    
    try {
      await recordInterestEmail({ email })
      e.currentTarget.reset()
      setMessage("Thanks! We'll email you when it's ready.")
    } catch (error: any) {
      setMessage(error?.message || "Something went wrong. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-lynx flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="text-6xl mb-4">🧾</div>
            <h1 className="text-4xl font-bold text-dusk mb-4">Receipt → Meal Plan</h1>
            <h2 className="text-xl text-muted-foreground mb-6">Coming Soon</h2>
            <p className="text-lg text-dusk leading-relaxed max-w-xl mx-auto">
              Turn your grocery receipts into a week of realistic recipes based on what you actually bought.
              Cut waste, save money, and stop the "what's for dinner?" spiral.
            </p>
          </div>

          {/* Interest Capture Card */}
          <Card className="border-0 shadow-lg rounded-lg">
            <CardContent className="p-6">
              {user ? (
                <>
                  <h3 className="text-xl font-semibold text-dusk mb-3">Tap to register your interest</h3>
                  <Button 
                    onClick={handleTap} 
                    disabled={isSubmitting}
                    className="w-full bg-spice-600 hover:bg-spice-700 text-white"
                  >
                    {isSubmitting ? 'Registering...' : 'I want this! 🙋‍♀️'}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-3">
                    We'll notify your account when the beta opens.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-dusk mb-3">Get notified when it launches</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email address</Label>
                      <Input 
                        id="email"
                        name="email" 
                        type="email" 
                        placeholder="you@example.com" 
                        required 
                        className="mt-1"
                      />
                    </div>
                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-spice-600 hover:bg-spice-700 text-white"
                    >
                      {isSubmitting ? 'Signing up...' : 'Notify me 📧'}
                    </Button>
                  </form>
                  <p className="text-sm text-muted-foreground mt-3">
                    Already have an account? Sign in to one-tap your interest.
                  </p>
                </>
              )}
              
              {message && (
                <div className={`mt-4 p-3 rounded-md text-sm ${
                  message.includes('Thanks') 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {message}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Value Proposition */}
          <Card className="border-0 shadow-lg rounded-lg">
            <CardHeader>
              <h4 className="text-xl font-semibold text-dusk">Why this matters</h4>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-dusk">
                <li className="flex items-start gap-2">
                  <span className="text-spice-600 font-bold mt-0.5">💸</span>
                  <span>Americans waste ~$1,500/year on unused groceries.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-spice-600 font-bold mt-0.5">🎯</span>
                  <span>Use what you bought; stop endless recipe scrolling.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-spice-600 font-bold mt-0.5">💰</span>
                  <span>$9.99/mo beats throwing away $125 each month.</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* How it works preview */}
          <Card className="border-0 shadow-lg rounded-lg">
            <CardHeader>
              <h4 className="text-xl font-semibold text-dusk">How it will work</h4>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl mb-2">📱</div>
                  <h5 className="font-medium text-dusk mb-1">Upload Receipt</h5>
                  <p className="text-sm text-muted-foreground">Take a photo or upload your grocery receipt</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">🤖</div>
                  <h5 className="font-medium text-dusk mb-1">AI Analysis</h5>
                  <p className="text-sm text-muted-foreground">Our AI identifies ingredients and quantities</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">📝</div>
                  <h5 className="font-medium text-dusk mb-1">Custom Meal Plan</h5>
                  <p className="text-sm text-muted-foreground">Get a week of recipes using your purchases</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
